import { useState, useEffect } from 'react';
import { FileText, ChevronDown, DollarSign } from 'lucide-react';
import { adminAxios } from './AdminLayout';
import { ComplianceReviewPanel, ComplianceBadge } from '../../components/admin/ComplianceReviewPanel';

var STATUS_LABELS = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700' },
  reviewing: { label: 'Reviewing', color: 'bg-yellow-100 text-yellow-700' },
  supplier_contacted: { label: 'Supplier Contacted', color: 'bg-purple-100 text-purple-700' },
  quoted: { label: 'Quoted', color: 'bg-cyan-100 text-cyan-700' },
  negotiating: { label: 'Negotiating', color: 'bg-orange-100 text-orange-700' },
  won: { label: 'Won', color: 'bg-green-100 text-green-700' },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-700' },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-500' },
};
var ALL_STATUSES = ['new', 'reviewing', 'supplier_contacted', 'quoted', 'negotiating', 'won', 'lost', 'closed'];

export default function AdminQuotes() {
  var [quotes, setQuotes] = useState([]);
  var [pipeline, setPipeline] = useState(null);
  var [loading, setLoading] = useState(true);
  var [filterStatus, setFilterStatus] = useState(null);
  var [expandedId, setExpandedId] = useState(null);

  function fetchAll() {
    var url = '/api/admin/quotes';
    if (filterStatus) url += '?status=' + filterStatus;
    adminAxios().get(url).then(function(r) { setQuotes(r.data); setLoading(false); });
    adminAxios().get('/api/admin/quotes/pipeline').then(function(r) { setPipeline(r.data); });
  }
  useEffect(function() { fetchAll(); }, [filterStatus]);

  async function updateStatus(quoteId, newStatus) {
    var notes = '';
    if (newStatus === 'won' || newStatus === 'lost') {
      notes = window.prompt('Add a note (optional):') || '';
    }
    await adminAxios().put('/api/admin/quotes/' + quoteId, { status: newStatus, notes: notes });
    fetchAll();
  }

  async function updateValue(quoteId) {
    var val = window.prompt('Enter estimated deal value:');
    if (val) {
      await adminAxios().put('/api/admin/quotes/' + quoteId, { status: quotes.find(function(q){return q.id===quoteId}).status, estimated_value: parseFloat(val) });
      fetchAll();
    }
  }

  return (
    <div data-testid="admin-quotes-page">
      <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-6">Quote Requests</h2>

      {/* Pipeline summary */}
      {pipeline && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
            <p className="font-space text-2xl font-bold text-slate-900">{pipeline.total_requests}</p>
            <p className="font-manrope text-[10px] text-slate-500">Total Requests</p>
          </div>
          <div className="bg-white rounded-2xl border border-blue-100 p-4 shadow-sm text-center">
            <p className="font-space text-2xl font-bold text-blue-600">{pipeline.new}</p>
            <p className="font-manrope text-[10px] text-blue-600">New</p>
          </div>
          <div className="bg-white rounded-2xl border border-orange-100 p-4 shadow-sm text-center">
            <p className="font-space text-2xl font-bold text-orange-600">{pipeline.negotiating}</p>
            <p className="font-manrope text-[10px] text-orange-600">Negotiating</p>
          </div>
          <div className="bg-white rounded-2xl border border-green-100 p-4 shadow-sm text-center">
            <p className="font-space text-2xl font-bold text-green-600">{pipeline.won}</p>
            <p className="font-manrope text-[10px] text-green-600">Won</p>
          </div>
          <div className="bg-white rounded-2xl border border-green-100 p-4 shadow-sm text-center">
            <p className="font-space text-xl font-bold text-green-600">{pipeline.conversion_rate}%</p>
            <p className="font-manrope text-[10px] text-green-600">Conversion</p>
          </div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={function(){setFilterStatus(null);}} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (!filterStatus ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')}>All</button>
        {ALL_STATUSES.map(function(s) {
          var info = STATUS_LABELS[s];
          return <button key={s} onClick={function(){setFilterStatus(s);}} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (filterStatus === s ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')}>{info.label}</button>;
        })}
      </div>

      {/* Quotes list */}
      {loading ? <div className="font-manrope text-slate-500">Loading...</div> :
       quotes.length === 0 ? <div className="text-center py-12 bg-white rounded-2xl border border-slate-100"><FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="font-manrope text-slate-500">No quote requests yet</p></div> :
       <div className="space-y-3">
        {quotes.map(function(q) {
          var info = STATUS_LABELS[q.status] || STATUS_LABELS.new;
          var expanded = expandedId === q.id;
          return (
            <div key={q.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="p-5 flex items-center justify-between cursor-pointer" onClick={function(){setExpandedId(expanded ? null : q.id);}}>
                <div>
                  <h4 className="font-manrope font-semibold text-slate-900">{q.company_name} — {q.contact_name}</h4>
                  <p className="font-manrope text-sm text-slate-500 mt-0.5">{q.products_requested}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={'px-2 py-0.5 rounded-full text-xs font-semibold ' + info.color}>{info.label}</span>
                    {q.compliance_status && q.compliance_status !== 'NOT_REQUIRED' && <ComplianceBadge status={q.compliance_status} testId={'quote-compliance-badge-' + q.id} />}
                    {q.desired_quantity && <span className="font-manrope text-xs text-slate-400">Qty: {q.desired_quantity}</span>}
                    {q.target_price && <span className="font-manrope text-xs text-slate-400">Target: {q.target_price} {q.preferred_currency}</span>}
                    {q.estimated_value > 0 && <span className="font-manrope text-xs text-green-600 font-semibold">Value: {q.preferred_currency || 'EUR'} {q.estimated_value.toLocaleString()}</span>}
                  </div>
                </div>
                <ChevronDown className={'w-5 h-5 text-slate-400 transition-transform ' + (expanded ? 'rotate-180' : '')} />
              </div>

              {expanded && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                    <div><span className="font-manrope text-xs text-slate-500">Email:</span> <span className="font-manrope text-slate-900">{q.email}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Phone:</span> <span className="font-manrope text-slate-900">{q.phone || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Country:</span> <span className="font-manrope text-slate-900">{q.country || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">VAT:</span> <span className="font-manrope text-slate-900">{q.vat_number || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Incoterm:</span> <span className="font-manrope text-slate-900">{q.required_incoterm || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Delivery:</span> <span className="font-manrope text-slate-900">{q.delivery_city || ''} {q.delivery_country || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Delivery Date:</span> <span className="font-manrope text-slate-900">{q.required_delivery_date || '-'}</span></div>
                    <div><span className="font-manrope text-xs text-slate-500">Created:</span> <span className="font-manrope text-slate-900">{q.created_at ? new Date(q.created_at).toLocaleDateString() : '-'}</span></div>
                  </div>
                  {q.comments && <div className="mb-4 p-3 bg-slate-50 rounded-lg"><span className="font-manrope text-xs text-slate-500">Comments:</span><p className="font-manrope text-sm text-slate-700 mt-1">{q.comments}</p></div>}

                  <div className="mb-4"><ComplianceReviewPanel data={q} source="quote" caseId={q.id} onChanged={fetchAll} /></div>

                  {/* Activity log */}
                  {q.activity_log && q.activity_log.length > 0 && (
                    <div className="mb-4">
                      <p className="font-manrope text-xs font-semibold text-slate-500 mb-2">Activity</p>
                      <div className="space-y-1">
                        {q.activity_log.map(function(a, i) {
                          return <div key={i} className="flex items-center gap-2 font-manrope text-xs">
                            <span className="text-slate-400">{a.timestamp ? new Date(a.timestamp).toLocaleString() : ''}</span>
                            <span className={'px-1.5 py-0.5 rounded text-[10px] font-semibold ' + (STATUS_LABELS[a.status] || STATUS_LABELS.new).color}>{(STATUS_LABELS[a.status] || {}).label || a.status}</span>
                            {a.notes && <span className="text-slate-600">{a.notes}</span>}
                          </div>;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {ALL_STATUSES.filter(function(s){return s !== q.status;}).map(function(s) {
                      return <button key={s} onClick={function(){updateStatus(q.id, s);}} className="px-3 py-1.5 rounded-lg border border-slate-200 font-manrope text-xs hover:bg-slate-50">{STATUS_LABELS[s].label}</button>;
                    })}
                    <button onClick={function(){updateValue(q.id);}} className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-green-200 text-green-700 font-manrope text-xs hover:bg-green-50"><DollarSign className="w-3 h-3" /> Set Value</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
       </div>
      }
    </div>
  );
}
